/**
 * #384 (brecha O-1) — REPARTO POR CANTIDAD entre bodega y compra.
 *
 * ===========================================================================
 * LANE PARITY — este archivo tiene un GEMELO BYTE-EQUIVALENTE en
 * `mcp-server/src/utils/materialSplit.js`.
 * ===========================================================================
 * No se importan entre sí a propósito: `src/**` es el bundle del browser y
 * `mcp-server/**` es un paquete Node aparte con su propio package.json (el
 * mismo motivo por el que `groupByAlcance` está duplicado en
 * `smartMaterialRequestTools.js` en vez de importarse de `src/utils/rfqExport.js`).
 * La duplicación es exactamente donde los carriles derivan (#353, #454), así que
 * hay un test de paridad que corre AMBAS copias contra la misma tabla de casos y
 * falla si dejan de coincidir: `mcp-server/tests/tools/materialSplitLaneParity.test.js`.
 * Si tocás una, tocá la otra.
 *
 * ---------------------------------------------------------------------------
 * EL PROBLEMA QUE RESUELVE
 * ---------------------------------------------------------------------------
 * Hasta ahora la aprobación repartía por `fulfillment_type` ENTERO:
 * `from_stock` XOR `needs_purchase`. Si Jorge necesitaba 100 tubos y en bodega
 * había 60, el sistema no podía decir "retirá 60 y comprá 40": o consumía los
 * 100 de stock (y fallaba) o compraba los 100 (y los 60 de bodega se quedaban
 * ahí). Acá se calcula el reparto real:
 *
 *     retirar = min(necesario, disponible)
 *     comprar = necesario − retirar
 *
 * ---------------------------------------------------------------------------
 * DE DÓNDE SALE "DISPONIBLE" — Y DE DÓNDE NO
 * ---------------------------------------------------------------------------
 * De `inventory.current_stock`, que es la ÚNICA autoridad de cuánto hay (#355).
 * NO de `inventory_batches`: en prod esa tabla está VACÍA y los 61 ítems son
 * `track_batches=false`, así que un `min()` contra lotes daría 0 para todo y
 * este fix terminaría comprando el 100% de cada ítem — el bug de nuevo, con más
 * código. El consumo FIFO sí usa lotes (es su mecanismo), pero la DECISIÓN de
 * cuánto retirar no puede depender de ellos.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ `fulfillment_type` YA NO RUTEA (y cuándo sí)
 * ---------------------------------------------------------------------------
 * `fulfillment_type` no es una elección humana: lo calcula la máquina con la
 * MISMA regla todo-o-nada que este archivo viene a reemplazar
 * (`matchInventoryItems`: `best.current_stock >= qty ? 'from_stock' : 'needs_purchase'`,
 * y su gemelo en `SmartMaterialRequest.js`). O sea: `needs_purchase` + un
 * `inventory_id` matcheado ES, literalmente, el caso O-1. Rutear por él sería
 * preguntarle la respuesta al bug.
 *
 * Entonces la regla es por DATO, no por bandera:
 *   - sin `inventory_id`  → 100% compra (no hay de qué retirar; incluye el botón
 *     "es un producto nuevo" de la UI, que limpia el inventory_id a propósito).
 *   - con `inventory_id` y stock CONOCIDO → reparto por min().
 *   - con `inventory_id` y stock DESCONOCIDO (no se pudo leer `inventory`, o la
 *     fila no vino por RLS) → se cae al ruteo VIEJO por `fulfillment_type`. Es
 *     degradación conservadora: sin saber cuánto hay, inventar un reparto es
 *     peor que repetir el comportamiento anterior, que al menos es el conocido.
 *     El caller reporta esa degradación en vez de callarla.
 */

/**
 * Redondeo a 2 decimales — `quantity_requested` es `numeric(10,2)` y la resta
 * en punto flotante deja migas (100 - 33.33 - 66.67 = 1.4e-14) que se colarían
 * como una línea de compra fantasma de 0.00000000000001 unidades.
 */
export const round2 = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round((v + Number.EPSILON) * 100) / 100;
};

/**
 * Calcula el reparto de una lista de ítems PENDIENTES de una solicitud.
 *
 * Es una función PURA: no toca la base, no consume nada, no decide estados.
 * Devuelve el plan; quien la llama ejecuta (FIFO + orden de compra) y persiste.
 *
 * El stock se lleva en un acumulador local porque DOS ítems de la misma
 * solicitud pueden apuntar al MISMO `inventory_id`. Sin el acumulador, ambos
 * creerían tener el stock completo disponible y se prometería dos veces la
 * misma unidad.
 *
 * @param {Array<object>} items - filas de `material_request_items` (pendientes)
 * @param {Map<string, number>|null} stockById - `current_stock` por inventory_id.
 *        `null`, o un id ausente del Map, significa STOCK DESCONOCIDO → ruteo
 *        viejo por `fulfillment_type` para ese ítem.
 * @returns {Array<{item:object, inventory_id:string|null, needed:number,
 *   available_stock:number|null, quantity_from_stock:number,
 *   quantity_to_purchase:number, stock_unknown:boolean, split:boolean}>}
 */
export function planMaterialSplit(items = [], stockById = null) {
  const known = stockById instanceof Map ? stockById : null;
  // Stock que va quedando a medida que los ítems se lo van llevando.
  const running = new Map();

  return (items || []).map((item) => {
    const needed = round2(parseFloat(item?.quantity_requested) || 0);
    const inventoryId = item?.inventory_id || null;

    // Sin inventario linkeado no hay nada que retirar: todo se compra. Igual
    // que antes de #384 — este caso nunca estuvo roto.
    if (!inventoryId || needed <= 0) {
      return {
        item,
        inventory_id: inventoryId,
        needed,
        available_stock: null,
        quantity_from_stock: 0,
        quantity_to_purchase: needed,
        stock_unknown: false,
        split: false,
      };
    }

    // Stock desconocido → ruteo viejo, explícitamente marcado para que el
    // caller pueda decir "esto NO se repartió y este es el motivo".
    if (!known || !known.has(inventoryId)) {
      const legacyFromStock = item?.fulfillment_type === 'from_stock' ? needed : 0;
      return {
        item,
        inventory_id: inventoryId,
        needed,
        available_stock: null,
        quantity_from_stock: legacyFromStock,
        quantity_to_purchase: round2(needed - legacyFromStock),
        stock_unknown: true,
        split: false,
      };
    }

    if (!running.has(inventoryId)) {
      const raw = parseFloat(known.get(inventoryId));
      running.set(inventoryId, Number.isFinite(raw) ? Math.max(0, round2(raw)) : 0);
    }
    const available = running.get(inventoryId);

    const fromStock = round2(Math.min(needed, available));
    const toPurchase = round2(needed - fromStock);
    running.set(inventoryId, round2(available - fromStock));

    return {
      item,
      inventory_id: inventoryId,
      needed,
      available_stock: available,
      quantity_from_stock: fromStock,
      quantity_to_purchase: toPurchase,
      stock_unknown: false,
      // "split" = el ítem se PARTIÓ de verdad (las dos patas > 0). Es lo único
      // que #384 agrega como caso nuevo; todo-stock y todo-compra ya existían.
      split: fromStock > 0 && toPurchase > 0,
    };
  });
}

/** Formatea una cantidad sin ceros de relleno: 60 → "60", 2.5 → "2.5". */
export const formatQty = (n) => String(round2(n));

/**
 * Tag de máquina que deja el reparto LEGIBLE en la fila, sin obligar a nadie a
 * joinear `stock_movements` con `purchase_items` para entender qué pasó.
 *
 * Vive en el namespace reservado de `notes` (ver MACHINE_TAG_RE en
 * `smartMaterialRequestTools.js`): sólo el servidor puede escribirlo, así que
 * su presencia PRUEBA que el reparto lo decidió la aprobación y no el modelo
 * (#368). Se estampa únicamente cuando el ítem se partió de verdad; en
 * todo-stock o todo-compra no aporta nada y sería ruido.
 */
export const splitNote = (fromStock, toPurchase, unit) => {
  const u = typeof unit === 'string' && unit.trim() && unit.trim() !== '(sin especificar)'
    ? ` ${unit.trim()}`
    : '';
  return `[REPARTO: ${formatQty(fromStock)}${u} de bodega + ${formatQty(toPurchase)}${u} por comprar]`;
};

/** Reemplaza el tag [REPARTO: ...] previo (si lo hubiera) por el actual. */
export const stampSplitNote = (existingNotes, fromStock, toPurchase, unit) => {
  const base = String(existingNotes || '')
    .replace(/\[REPARTO:[^\]]*\]/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return [base, splitNote(fromStock, toPurchase, unit)].filter(Boolean).join(' ');
};

const materialSplit = { round2, planMaterialSplit, formatQty, splitNote, stampSplitNote };
export default materialSplit;
